/**
 * Choosing how many photographs a surface model may use, and being told what
 * that changes.
 *
 * It was a bare `<select>` reading "60 foto", with no statement of what the
 * other two did. The three options run identical dense settings and differ
 * only in how many views they may take and therefore how long PatchMatch runs,
 * so that is what the copy says. None of them promises a better model: nothing
 * has measured that, and the profiles differ in *time budget* rather than in
 * quality.
 *
 * A radio group rather than a select, because each option carries a sentence
 * and a select cannot show one. Native radios, so keyboard and screen-reader
 * behaviour is the platform's; the selected state is a word and a control
 * state, never colour alone.
 */

import { Callout } from "@/components/ui";
import { RECONSTRUCTION_TERMS } from "@/i18n/terms";
import {
  SURFACE_OPTIONS,
  type SurfaceAvailability,
  type SurfaceOption,
} from "./reconstructionState";

export function SurfaceOptions({
  value,
  availability,
  disabled = false,
  onChange,
}: {
  value: number;
  availability: SurfaceAvailability;
  disabled?: boolean;
  onChange: (images: number) => void;
}) {
  const inert = disabled || availability !== "available";
  return (
    <fieldset className="surface-options" disabled={inert}>
      <legend className="surface-options__legend">Kaç fotoğraf kullanılsın?</legend>
      {SURFACE_OPTIONS.map((option: SurfaceOption) => (
        <label key={option.profile} className="surface-options__option">
          <input
            type="radio"
            name="surface-images"
            value={option.images}
            checked={value === option.images}
            onChange={() => onChange(option.images)}
          />
          <span>
            <span className="surface-options__label">{option.label}</span>
            <span className="surface-options__note">{option.description}</span>
          </span>
        </label>
      ))}
      {/*
        No minutes. One measured production run is not a service level, and a
        number here would be read as one - so the honest statement is the shape
        of the difference and what it actually depends on.
      */}
      <p className="faint surface-options__caveat">
        Kesin süre fotoğraf sayısına ve çekimin kendisine göre değişir.
      </p>
    </fieldset>
  );
}

/**
 * Why the surface feature cannot be started right now.
 *
 * Present rather than absent. The whole control used to disappear when no
 * capable machine was connected, which produced the only question a user
 * cannot answer from the screen: where did my buttons go?
 *
 * Three reasons, three sentences. They were two, and the missing one was the
 * live case: a connected worker without GPU support was described as a
 * disconnected server. One table, keyed by the state, so a fourth reason
 * cannot be added without wording it.
 */
const SURFACE_BLOCKED: Record<
  Exclude<SurfaceAvailability, "available">,
  { title: string; note: string }
> = {
  offline: {
    title: RECONSTRUCTION_TERMS.surfaceUnavailable,
    note: RECONSTRUCTION_TERMS.surfaceUnavailableNote,
  },
  no_gpu: {
    title: RECONSTRUCTION_TERMS.surfaceNoGpu,
    note: RECONSTRUCTION_TERMS.surfaceNoGpuNote,
  },
  unknown: {
    title: RECONSTRUCTION_TERMS.surfaceUnknown,
    note: RECONSTRUCTION_TERMS.surfaceUnknownNote,
  },
};

export function SurfaceUnavailableNote({ availability }: { availability: SurfaceAvailability }) {
  if (availability === "available") return null;
  const { title, note } = SURFACE_BLOCKED[availability];
  return (
    <Callout tone="neutral" title={title}>
      {note}
    </Callout>
  );
}
